import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
// --- FIX APPLIED HERE: Added ChevronDown to the import list ---
import { Search, Mail, Phone, MapPin, Briefcase, Filter, X, ChevronDown } from 'lucide-react';

// --- MOCK API DATA ---
const mockEmployees = [
  { id: 1, name: 'Alice Johnson', title: 'Senior Developer', department: 'Engineering', location: 'New York', email: 'alice@corp.com', phone: '555-0101', photo: 'https://i.pravatar.cc/150?img=1' },
  { id: 2, name: 'Bob Smith', title: 'Product Manager', department: 'Product', location: 'Remote', email: 'bob@corp.com', phone: '555-0102', photo: 'https://i.pravatar.cc/150?img=2' },
  { id: 3, name: 'Charlie Brown', title: 'UX Designer', department: 'Design', location: 'San Francisco', email: 'charlie@corp.com', phone: '555-0103', photo: 'https://i.pravatar.cc/150?img=3' },
  { id: 4, name: 'Danaerys Targaryen', title: 'HR Specialist', department: 'Human Resources', location: 'New York', email: 'danaerys@corp.com', phone: '555-0104', photo: 'https://i.pravatar.cc/150?img=4' },
  { id: 5, name: 'Edward Norton', title: 'Software Architect', department: 'Engineering', location: 'Remote', email: 'edward@corp.com', phone: '555-0105', photo: 'https://i.pravatar.cc/150?img=5' },
];

// --- API MOCK FUNCTION ---
const fetchEmployeeData = async () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ data: mockEmployees });
    }, 800); // Simulate network delay
  });
};

// --- FRAMER MOTION VARIANTS ---
const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.1,
    },
  },
};

const cardItem = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
};

// --- UTILITY COMPONENT: EmployeeCard ---
const EmployeeCard = ({ employee }) => {
  return (
    <motion.div
      layout
      variants={cardItem}
      whileHover={{ scale: 1.03, boxShadow: '0 15px 25px rgba(0, 0, 0, 0.15)' }}
      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-t-4 border-indigo-500 hover:border-blue-600 transition-all duration-300 transform flex flex-col items-center text-center"
    >
      <img
        className="w-24 h-24 rounded-full object-cover mb-4 ring-4 ring-indigo-200 dark:ring-indigo-700"
        src={employee.photo}
        alt={`Photo of ${employee.name}`}
        onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=User' }} // Fallback
      />
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{employee.name}</h3>
      <p className="text-indigo-600 dark:text-indigo-400 font-medium mt-1">{employee.title}</p>
      
      <div className="mt-4 w-full border-t border-gray-100 dark:border-gray-700 pt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
        <p className="flex items-center justify-center">
          <Briefcase size={16} className="mr-2 text-indigo-400" /> {employee.department}
        </p>
        <p className="flex items-center justify-center">
          <MapPin size={16} className="mr-2 text-indigo-400" /> {employee.location}
        </p>
      </div>

      <div className="mt-4 flex space-x-4">
        <motion.a
          href={`mailto:${employee.email}`}
          whileHover={{ scale: 1.15 }}
          className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/50 dark:hover:bg-blue-900 transition-colors"
          aria-label={`Email ${employee.name}`}
        >
          <Mail size={18} />
        </motion.a>
        <motion.a
          href={`tel:${employee.phone}`}
          whileHover={{ scale: 1.15 }}
          className="p-2 rounded-full bg-green-100 hover:bg-green-200 text-green-600 dark:bg-green-900/50 dark:hover:bg-green-900 transition-colors"
          aria-label={`Call ${employee.name}`}
        >
          <Phone size={18} />
        </motion.a>
      </div>
    </motion.div>
  );
};

// --- MAIN COMPONENT: CompanyDirectory ---
const CompanyDirectory = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    fetchEmployeeData().then(response => {
      setEmployees(response.data);
      setLoading(false);
    });
  }, []);

  // Use useMemo for efficient filtering
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const searchMatch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          employee.title.toLowerCase().includes(searchTerm.toLowerCase());
      
      const departmentMatch = selectedDepartment === '' || employee.department === selectedDepartment;

      return searchMatch && departmentMatch;
    });
  }, [employees, searchTerm, selectedDepartment]);

  // Extract unique departments for the filter dropdown
  const departments = useMemo(() => {
    const all = employees.map(e => e.department);
    return ['', ...new Set(all)].sort(); // Add empty string for "All" option
  }, [employees]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="text-indigo-600"
        >
          <Briefcase size={48} />
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={listVariants}
      className="p-4 sm:p-8 md:p-12 min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-500"
    >
      
      {/* Header */}
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="mb-8 md:mb-12 border-b-4 border-blue-500 pb-4"
      >
        <h1 className="text-4xl font-extrabold text-blue-800 dark:text-blue-400 tracking-tight">
          Company Directory
        </h1>
        <p className="mt-1 text-lg text-gray-500 dark:text-gray-400">
          Find colleagues quickly by name, title, or department.
        </p>
      </motion.header>

      {/* Search and Filters Bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Search Input */}
        <motion.div 
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="relative flex-grow"
        >
          <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full py-3 pl-10 pr-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 transition-all shadow-sm"
          />
        </motion.div>

        {/* Filter Button (Mobile/Tablet) */}
        <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="md:hidden py-3 px-6 bg-indigo-500 text-white rounded-xl shadow-md hover:bg-indigo-600 transition-colors flex items-center justify-center"
        >
            <Filter size={20} className="mr-2" />
            {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
        </motion.button>

        {/* Department Filter (Desktop and open Mobile) */}
        <motion.div
            initial={false}
            animate={{ height: isFilterOpen || window.innerWidth >= 768 ? 'auto' : 0, opacity: isFilterOpen || window.innerWidth >= 768 ? 1 : 0 }}
            className={`overflow-hidden ${isFilterOpen ? 'block' : 'hidden md:block'}`}
        >
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-sm border border-gray-300 dark:border-gray-700">
            <Filter size={20} className="text-indigo-500 ml-2" />
            <div className="relative">
              <select
                value={selectedDepartment}
                onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    if (window.innerWidth < 768) setIsFilterOpen(false); // Close filter on mobile after selection
                }}
                className="appearance-none bg-transparent py-2 pl-3 pr-8 text-sm font-medium text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md"
              >
                <option value="">All Departments</option>
                {departments.filter(d => d !== '').map(department => (
                  <option key={department} value={department}>{department}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-indigo-500" />
            </div>
            {selectedDepartment && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setSelectedDepartment('')}
                className="p-1 text-red-500 hover:text-red-700"
              >
                <X size={16} />
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Employee Cards Grid */}
      {filteredEmployees.length > 0 ? (
        <motion.div
          layout // Enables layout animations for cards when filtered
          variants={listVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
        >
          <AnimatePresence>
            {filteredEmployees.map(employee => (
              <EmployeeCard key={employee.id} employee={employee} />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div 
            key="not-found"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-12 text-center bg-white dark:bg-gray-800 rounded-xl shadow-lg mt-10"
        >
            <Search size={48} className="mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">No Employees Found</h2>
            <p className="text-gray-500 dark:text-gray-400">Please adjust your search term or filters.</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CompanyDirectory;